"use client"

import { type CategoryTranslations } from "@/app/(site)/_components/Organisms/ProductGrids/_utils/categoryTranslations"
import {
    type FilterState,
    type FilterType,
    filterReducer
} from "@/app/(site)/_components/Organisms/ProductGrids/filterReducer"
import { mapSearchParamsToFilters } from "@/app/(site)/_components/Organisms/ProductGrids/helpers"
import { sortConfig } from "@/app/(site)/_components/Organisms/ProductGrids/types/options"
import type { SearchFilter } from "@/lib/depict-client/types/api/ProductListingRequestV3"
import { type SortModel } from "@/lib/depict-client/types/api/ProductListingRequestV3"
import type { FacetMappings } from "@/lib/facet-mappings"
import logger from "@/lib/logger/logger-init"
import { type SiteContext } from "@/lib/utils/tracking/siteContext"
import { usePathname } from "next/navigation"
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState
} from "react"
import toast from "react-hot-toast"

export enum DesktopViewMode {
    Four = 4,
    Six = 6
}

export enum MobileViewMode {
    Two = 2,
    Three = 3
}

// --- Filter Panel Context (changes on filter panel open/close) ---

export type FilterPanelContextValue = {
    isFilterPanelOpen: boolean
    toggleFilterPanel: () => void
}

const FilterPanelContext = createContext<FilterPanelContextValue | null>(null)

export function useFilterPanelContext() {
    const ctx = useContext(FilterPanelContext)
    if (!ctx) {
        throw new Error("useFilterPanelContext must be used within a ProductGridProvider")
    }
    return ctx
}

// --- Static Context (set once, never changes) ---

export type StaticGridContextValue = {
    categoryTranslations: CategoryTranslations
    initialFilters: SearchFilter[]
    sortSplitIndex: number
    sortOpen: boolean
    initialOpenFields: string[]
    facetMappings: FacetMappings
}

const StaticGridContext = createContext<StaticGridContextValue | null>(null)

export function useStaticGridContext() {
    const ctx = useContext(StaticGridContext)
    if (!ctx) {
        throw new Error("useStaticGridContext must be used within a ProductGridProvider")
    }
    return ctx
}

// --- Dynamic Context (changes on filter/sort/mobile filter actions) ---

export type ProductGridContextValue = {
    filterState: FilterState
    setFilter: (
        field: string,
        op: string,
        value: string | string[] | number | number[]
    ) => void
    clearFilter: (field: string, value: string) => void
    clearAllFilters: () => void
    setSort: (sort: string) => void
    clearSort: () => void
    hasFiltersOrSort: boolean

    isMobileFilterOpen: boolean
    openMobileFilter: () => void
    closeMobileFilter: () => void
}

const ProductGridContext = createContext<ProductGridContextValue | null>(null)

export function useProductGridContext() {
    const ctx = useContext(ProductGridContext)
    if (!ctx) {
        throw new Error("useProductGridContext must be used within a ProductGridProvider")
    }
    return ctx
}

// --- Provider ---

function buildFilterUrl(
    currentSearch: string,
    pathname: string,
    filters: FilterType[],
    sort: SortModel | undefined,
    allowedFacets: Set<string>
) {
    // Preserve any unrelated query params (UTMs, _kx, gclid, fbclid, etc.)
    // so analytics/attribution survives filter interactions on the PLP.
    const searchParams = new URLSearchParams(currentSearch)

    allowedFacets.forEach(field => searchParams.delete(field))
    searchParams.delete("sort")
    // Filter changes reset pagination — matches prior behaviour.
    searchParams.delete("page")
    searchParams.delete("cursor")

    filters.forEach(option => {
        if (option.data && option.data.length > 0) {
            searchParams.set(option.field, option.data.join("&"))
        }
    })

    if (sort) {
        searchParams.set("sort", `${sort.field}&${sort.order}`)
    }

    const qs = searchParams.toString()
    return qs ? `${pathname}?${qs}` : pathname
}

type ProductGridProviderProps = {
    children: React.ReactNode
    initialFilterQuery: FilterState
    siteContext: SiteContext
    listHierarchy: string
    categoryTranslations: CategoryTranslations
    initialFilters: SearchFilter[]
    sortSplitIndex: number
    sortOpen: boolean
    initialOpenFields: string[]
    facetMappings: FacetMappings
    filterOpenRef?: React.RefObject<boolean>
    onFilterToggle?: () => void
}

export function ProductGridProvider({
    children,
    initialFilterQuery,
    siteContext,
    listHierarchy,
    categoryTranslations,
    initialFilters,
    sortSplitIndex,
    sortOpen,
    initialOpenFields,
    facetMappings,
    filterOpenRef,
    onFilterToggle
}: ProductGridProviderProps) {
    const pathname = usePathname()
    const [filterState, dispatch] = useReducer(filterReducer, initialFilterQuery)
    const prevFilterStateRef = useRef(filterState)

    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false)
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)

    const filterCacheKey = filterState.filters
        .map(f => `${f.field}:${f.data.join(",")}`)
        .join("|")
    const sortCacheKey = filterState.sort
        ? `${filterState.sort.field}:${filterState.sort.order}`
        : ""

    // Sync filter/sort state to the URL via `window.history` (no RSC roundtrip,
    // so the "Above content" modules don't re-render and images don't flicker).
    // To recover the correct filter state on back-navigation from a PDP — where
    // Next's router cache may serve the unfiltered RSC tree — we hydrate the
    // reducer from the URL on mount and on `popstate` (see the effect below).
    useEffect(() => {
        const hasFiltersChanged =
            JSON.stringify(prevFilterStateRef.current.filters) !==
            JSON.stringify(filterState.filters)
        const hasSortChanged =
            JSON.stringify(prevFilterStateRef.current.sort) !==
            JSON.stringify(filterState.sort)

        if (hasFiltersChanged || hasSortChanged) {
            prevFilterStateRef.current = filterState
            const url = buildFilterUrl(
                window.location.search,
                pathname,
                filterState.filters,
                filterState.sort,
                facetMappings.allowedFacets
            )
            window.history.replaceState(null, "", url)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCacheKey, sortCacheKey, pathname])

    // Read `window.location.search` into a plain `{ [key]: string }` map that
    // `mapSearchParamsToFilters` can consume.
    const readFilterStateFromUrl = useCallback((): FilterState => {
        const params = new URLSearchParams(window.location.search)
        const obj: { [key: string]: string } = {}
        params.forEach((value, key) => {
            obj[key] = value
        })
        return mapSearchParamsToFilters(
            obj,
            false,
            undefined,
            facetMappings.allowedFacets
        )
    }, [facetMappings.allowedFacets])

    // Hydrate filter state from the URL:
    //   1. Once on mount — catches the case where Next served a cached (stale)
    //      unfiltered RSC for a URL that actually carries filter params (e.g.
    //      browser back from a PDP). If initialFilterQuery already matches the
    //      URL, dispatch is skipped so SSR renders aren't fought.
    //   2. On every `popstate` — catches back/forward within PLP history.
    useEffect(() => {
        const syncFromUrl = () => {
            const fromUrl = readFilterStateFromUrl()
            const sameFilters =
                JSON.stringify(fromUrl.filters) ===
                JSON.stringify(prevFilterStateRef.current.filters)
            const sameSort =
                JSON.stringify(fromUrl.sort) ===
                JSON.stringify(prevFilterStateRef.current.sort)
            if (sameFilters && sameSort) return

            // Pre-update the diff ref so the URL-sync effect above sees the
            // reducer's new state as "already synced" and skips a redundant
            // replaceState.
            prevFilterStateRef.current = fromUrl
            dispatch({ type: "HYDRATE_FROM_URL", payload: fromUrl })
        }

        syncFromUrl()
        window.addEventListener("popstate", syncFromUrl)
        return () => window.removeEventListener("popstate", syncFromUrl)
    }, [readFilterStateFromUrl])

    const setFilter = useCallback(
        (field: string, op: string, value: string | string[] | number | number[]) => {
            if (!field) {
                logger.error("setFilter: Missing 'field' parameter.")
                return
            }

            const data = Array.isArray(value) ? value.map(String) : [String(value)]

            try {
                dispatch({ type: "SET_FILTER", payload: { field, op, data } })
            } catch (error) {
                logger.error(
                    `setFilter: Error dispatching filter for field "${field}" with operation "${op}" and data:`,
                    data,
                    error
                )
                toast.error("An error occurred while applying the filter.")
            }
        },
        []
    )

    const clearFilter = useCallback((field: string, value: string) => {
        dispatch({ type: "CLEAR_FILTER", payload: { field, value } })
    }, [])

    const clearAllFilters = useCallback(() => {
        dispatch({ type: "CLEAR_ALL_FILTERS" })
    }, [])

    const setSort = useCallback(
        (sortKey: string): void => {
            const sortOption = sortConfig[sortKey]
            if (!sortOption) {
                logger.error(
                    `Invalid sort key: ${sortKey}. Available keys are: ${Object.keys(sortConfig).join(", ")}`
                )
                return
            }
            dispatch({ type: "SORT_PRODUCTS", payload: sortOption })
            import("@/lib/utils/tracking/gtmEvents").then(({ sortEvent }) => {
                sortEvent(
                    String(sortKey),
                    sortOption.meta?.title || String(sortKey),
                    siteContext,
                    listHierarchy
                )
            })
        },
        [siteContext, listHierarchy]
    )

    const clearSort = useCallback(() => {
        dispatch({ type: "CLEAR_SORT" })
        import("@/lib/utils/tracking/gtmEvents").then(({ sortEvent }) => {
            sortEvent("recommended", "Recommended", siteContext, listHierarchy)
        })
    }, [siteContext, listHierarchy])

    const hasFiltersOrSort =
        filterState.filters.length > 0 || filterState.sort !== undefined

    const toggleFilterPanel = useCallback(() => {
        const next = !isFilterPanelOpen
        if (filterOpenRef) {
            ;(filterOpenRef as React.RefObject<boolean>).current = next
        }
        setIsFilterPanelOpen(next)
        onFilterToggle?.()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterPanelOpen, filterOpenRef])

    const openMobileFilter = useCallback(() => {
        setIsMobileFilterOpen(true)
    }, [])

    const closeMobileFilter = useCallback(() => {
        setIsMobileFilterOpen(false)
    }, [])

    const filterPanelValue = useMemo<FilterPanelContextValue>(
        () => ({
            isFilterPanelOpen,
            toggleFilterPanel
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isFilterPanelOpen]
    )

    const staticValue = useMemo<StaticGridContextValue>(
        () => ({
            categoryTranslations,
            initialFilters,
            sortSplitIndex,
            sortOpen,
            initialOpenFields,
            facetMappings
        }),
        [
            categoryTranslations,
            initialFilters,
            sortSplitIndex,
            sortOpen,
            initialOpenFields,
            facetMappings
        ]
    )

    const dynamicValue = useMemo<ProductGridContextValue>(
        () => ({
            filterState,
            setFilter,
            clearFilter,
            clearAllFilters,
            setSort,
            clearSort,
            hasFiltersOrSort,
            isMobileFilterOpen,
            openMobileFilter,
            closeMobileFilter
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filterCacheKey, sortCacheKey, isMobileFilterOpen]
    )

    return (
        <StaticGridContext.Provider value={staticValue}>
            <FilterPanelContext.Provider value={filterPanelValue}>
                <ProductGridContext.Provider value={dynamicValue}>
                    {children}
                </ProductGridContext.Provider>
            </FilterPanelContext.Provider>
        </StaticGridContext.Provider>
    )
}
